import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Activity, ArrowRight, Loader2, Mail, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { useAuth } from "../contexts/AuthContext";

export default function AuthPage({ mode = "login" }) {
  const isSignup = mode === "signup";
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, login } = useAuth();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    clinic_name: "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (isSignup) {
        if (form.password.length < 8) {
          throw new Error("Password must be at least 8 characters.");
        }
        await signup(form);
        toast.success("Welcome to TherapyNote", {
          description: "Your 15-day free trial starts now.",
        });
      } else {
        await login({ email: form.email, password: form.password });
        toast.success("Signed in");
      }
      const target = location.state?.from || "/app";
      navigate(target, { replace: true });
    } catch (err) {
      // 409 from signup → account already exists. Steer them to sign in
      // (or Google) instead of leaving them stuck.
      const status = err?.response?.status || err?.status;
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        (isSignup ? "Couldn't sign up" : "Couldn't sign in");
      if (isSignup && (status === 409 || /already exists/i.test(detail))) {
        toast.error("This email is already registered", {
          description: "Sign in instead, or use Continue with Google.",
          action: {
            label: "Sign in",
            onClick: () => navigate("/login", { state: location.state }),
          },
        });
      } else {
        toast.error(isSignup ? "Couldn't sign up" : "Couldn't sign in", {
          description: detail,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FAFAFA] flex flex-col"
      data-testid={isSignup ? "signup-page" : "login-page"}
    >
      <Toaster position="top-center" richColors />
      <div className="px-6 md:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#10B981] text-white shadow-[0_4px_12px_rgba(16,185,129,0.35)]">
            <Activity className="w-5 h-5" strokeWidth={2.5} />
          </span>
          <span className="font-outfit text-xl font-bold tracking-tight text-[#0F172A]">
            TherapyNote
          </span>
        </Link>
        <Link
          to={isSignup ? "/login" : "/signup"}
          className="font-figtree text-sm text-[#475569] hover:text-[#059669]"
        >
          {isSignup ? "Already have an account? Sign in" : "New here? Create account"}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_30px_60px_rgba(15,23,42,0.06)] p-8 md:p-10">
            <div className="text-center mb-6">
              <h1 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A]">
                {isSignup ? "Start your 15-day trial" : "Welcome back"}
              </h1>
              <p className="mt-2 font-figtree text-sm text-[#64748B]">
                {isSignup
                  ? "Free for 15 days. No credit card required."
                  : "Sign in to your TherapyNote account."}
              </p>
            </div>

            {/* Google — primary */}
            <button
              type="button"
              data-testid="auth-google-btn"
              onClick={handleGoogle}
              className="w-full h-12 rounded-2xl bg-[#0F172A] hover:bg-[#1E293B] text-white font-figtree text-base font-medium flex items-center justify-center gap-3 transition-all duration-300 hover:-translate-y-0.5 shadow-[0_8px_20px_rgba(15,23,42,0.25)]"
            >
              <GoogleGlyph />
              Continue with Google
            </button>

            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="font-figtree text-xs text-[#94A3B8] uppercase tracking-[0.18em]">
                or with email
              </span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {isSignup && (
                <>
                  <div>
                    <Label className="font-figtree text-xs text-[#475569]">Your name</Label>
                    <Input
                      data-testid="auth-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Dr. Alex Chen"
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
                    />
                  </div>
                  <div>
                    <Label className="font-figtree text-xs text-[#475569]">Clinic name</Label>
                    <Input
                      data-testid="auth-clinic"
                      value={form.clinic_name}
                      onChange={(e) => update("clinic_name", e.target.value)}
                      placeholder="Riverside Sports PT"
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
                    />
                  </div>
                </>
              )}
              <div>
                <Label className="font-figtree text-xs text-[#475569]">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    type="email"
                    required
                    data-testid="auth-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@clinic.com"
                    className="pl-9 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="font-figtree text-xs text-[#475569]">Password</Label>
                  {!isSignup && (
                    <Link
                      to="/forgot-password"
                      data-testid="auth-forgot-link"
                      className="font-figtree text-xs text-[#10B981] hover:text-[#059669]"
                    >
                      Forgot password?
                    </Link>
                  )}
                </div>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    type="password"
                    required
                    minLength={isSignup ? 8 : undefined}
                    data-testid="auth-password"
                    value={form.password}
                    onChange={(e) => update("password", e.target.value)}
                    placeholder={isSignup ? "At least 8 characters" : "Your password"}
                    className="pl-9 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="auth-submit"
                disabled={busy}
                className="w-full h-11 mt-2 rounded-2xl bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium disabled:opacity-50"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Working…
                  </>
                ) : (
                  <>
                    {isSignup ? "Create account & start trial" : "Sign in"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-5 text-center font-figtree text-xs text-[#94A3B8]">
              By continuing you agree to our terms. No credit card required.
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/demo"
              className="font-figtree text-sm text-[#475569] hover:text-[#059669]"
            >
              Just exploring? Try the demo →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white">
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M23.5 12.3c0-.8-.07-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.8-2.4 3.7v3.1h3.9c2.3-2.1 3.5-5.2 3.5-8.9z"
        />
        <path
          fill="#34A853"
          d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3.1c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1C3.4 21.4 7.4 24 12 24z"
        />
        <path
          fill="#FBBC05"
          d="M5.4 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l4-3.1z"
        />
        <path
          fill="#EA4335"
          d="M12 4.7c1.8 0 3.4.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.6l4 3.1C6.3 6.8 8.9 4.7 12 4.7z"
        />
      </svg>
    </span>
  );
}
